<script setup>
import Layout from "../../layouts/main.vue";
import MethodService from "../../service/MethodService";
import mushroom from "cem-primary-api";
import { onMounted, reactive, ref, nextTick, computed } from "vue";
import DataForm from "./dataService";
import { FormInstance } from "element-plus";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import toastr from "toastr";
import Swal from "sweetalert2";
import { marked } from "marked";
import DOMPurify from "dompurify";

const noneIcon = require("@/assets/icon/none-icon-svg.svg");

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const ruleFormRef = ref(FormInstance);
const loading = ref(false);

const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const validForm = reactive({ value: DataForm.tableRules.dataSearch.valid });
const dataTables = reactive({ value: [] });

const editVisible = ref(false);
const editFormRef = ref(null);
const editForm = reactive(MethodService.copyObject(DataForm.form));
const editRules = reactive(DataForm.rules);
const saving = ref(false);
const categories = ref([]);
const dialogType = ref("add"); // "add" hoặc "edit"
const showViewDialog = ref(false);
const viewData = ref("");
const iconType = ref("link"); // "link" hoặc "upload"
const fileInput = ref(null);
const originalServiceName = ref("");

const dialogTitle = computed(() => {
  if (dialogType.value === "add") return t("t_add_service");
  return (
    t("t_edit_service") +
    (originalServiceName.value ? ": " + originalServiceName.value : "")
  );
});

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const typeImage = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const allowedTypes = [".jpg", ".png", ".jpeg", ".svg"];

  if (!allowedTypes.includes(typeImage)) {
    toastr.warning(t("t_select_format_image"));
    event.target.value = "";
    return;
  }

  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await mushroom.service_health_ch.uploadIconAsync({
      body: formData,
      settings: {
        isRawData: true,
        extra: {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      },
    });
    if (response.result) {
      editForm.icon_display =
        location.origin + "/logo/" + response.result.file_name;
    }
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    event.target.value = "";
  }
};

const updateUrlQuery = (extraParams = {}) => {
  const searchValues = {};
  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    const val = tableRules.dataSearch.value[key];
    if (val !== "" && val !== null && val !== undefined) {
      searchValues[key] = val;
    }
  });
  const query = {
    ...searchValues,
    _limit: tableRules.limit,
    _offset: tableRules.offset,
    _sort: tableRules.sort,
    showFormSearch: tableRules.showFormSearch,
    page: tableRules.page,
    ...extraParams,
  };
  router.replace({ query }).catch(() => {});
};

const syncStateFromUrl = () => {
  const q = route.query;
  if (q.limit) tableRules.limit = parseInt(q.limit);
  if (q.page) tableRules.page = parseInt(q.page);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;

  if (q._sort) {
    tableRules.sort = q._sort;
    tableRules.defaultSort[0] = tableRules.sort.replace("-", "");
    tableRules.defaultSort[1] = tableRules.sort.startsWith("-")
      ? "descending"
      : "ascending";
  }

  if (q.showFormSearch) {
    tableRules.showFormSearch = q.showFormSearch === "true";
  }

  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    if (q[key] !== undefined) tableRules.dataSearch.value[key] = q[key];
  });
};

const getCategories = async () => {
  try {
    const res = await mushroom.service_health_category.views.getListAsync({
      _limit: 1000,
    });
    categories.value = res.result || [];
  } catch (e) {
    console.error("Error fetching categories:", e);
  }
};

const getGroupName = (id) => {
  const cat = categories.value.find((c) => c.group_id == id);
  return cat ? cat.group_name : id;
};

const markdownToHTML = (text) => {
  if (text) {
    const html = marked(text);
    return DOMPurify.sanitize(html);
  }
  return "";
};

const getListData = async () => {
  loading.value = true;
  updateUrlQuery();

  const searchParams = {};
  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    const val = tableRules.dataSearch.value[key];
    if (val !== "" && val !== null && val !== undefined) {
      searchParams[key] = val;
    }
  });
  if (searchParams.main_group_id) {
    searchParams.main_group_id = parseInt(searchParams.main_group_id);
  }
  const isPriorityFilter = searchParams.is_priority;

  try {
    if (isPriorityFilter === "1") {
      const countPriorityRes =
        await mushroom.service_health_ch.views.getCountPriorityAsync(
          searchParams,
        );
      tableRules.total = countPriorityRes.result?.[0]?.total || 0;

      const res = await mushroom.service_health_ch.views.getListPriorityAsync({
        ...searchParams,
        _offset: tableRules.offset,
        _limit: tableRules.limit,
        _sort: tableRules.sort,
      });
      dataTables.value = (res.result || []).map((item) => ({
        ...item,
        is_priority: 1,
      }));
      return;
    }

    if (isPriorityFilter === "0") {
      const countRegularRes =
        await mushroom.service_health_ch.views.getCountNoPriorityAsync(
          searchParams,
        );
      tableRules.total = countRegularRes.result?.[0]?.total || 0;

      const res = await mushroom.service_health_ch.views.getListNoPriorityAsync(
        {
          ...searchParams,
          _offset: tableRules.offset,
          _limit: tableRules.limit,
          _sort: tableRules.sort,
        },
      );
      dataTables.value = (res.result || []).map((item) => ({
        ...item,
        is_priority: 0,
      }));
      return;
    }

    // 1. Lấy tổng số lượng của cả 2 danh sách để tính toán phân trang
    const [countPriorityRes, countRegularRes] = await Promise.all([
      mushroom.service_health_ch.views.getCountPriorityAsync(searchParams),
      mushroom.service_health_ch.views.getCountNoPriorityAsync(searchParams),
    ]);

    const totalPriority = countPriorityRes.result?.[0]?.total || 0;
    const totalRegular = countRegularRes.result?.[0]?.total || 0;
    tableRules.total = totalPriority + totalRegular;

    const globalOffset = tableRules.offset;
    const globalLimit = tableRules.limit;

    let priorityData = [];
    let regularData = [];
    const fetchTasks = [];

    // 2. Tính toán xem trang hiện tại cần lấy bao nhiêu từ danh sách Priority và bao nhiêu từ Regular
    // Nếu offset vẫn nằm trong phạm vi của danh sách Priority
    if (globalOffset < totalPriority) {
      const pOffset = globalOffset;
      const pLimit = Math.min(globalLimit, totalPriority - globalOffset);

      fetchTasks.push(
        mushroom.service_health_ch.views
          .getListPriorityAsync({
            ...searchParams,
            _offset: pOffset,
            _limit: pLimit,
            _sort: tableRules.sort,
          })
          .then((res) => {
            priorityData = (res.result || []).map((item) => ({
              ...item,
              is_priority: 1,
            }));
          }),
      );

      // Nếu trang hiện tại vẫn còn chỗ trống sau khi lấy hết Priority có thể lấy
      const remainingLimit = globalLimit - pLimit;
      if (remainingLimit > 0 && totalRegular > 0) {
        fetchTasks.push(
          mushroom.service_health_ch.views
            .getListNoPriorityAsync({
              ...searchParams,
              _offset: 0,
              _limit: remainingLimit,
              _sort: tableRules.sort,
            })
            .then((res) => {
              regularData = (res.result || []).map((item) => ({
                ...item,
                is_priority: item.is_priority === 1 ? 1 : 0,
              }));
            }),
        );
      }
    } else {
      // Nếu offset đã vượt qua danh sách Priority, chỉ lấy từ danh sách Regular
      const rOffset = globalOffset - totalPriority;
      fetchTasks.push(
        mushroom.service_health_ch.views
          .getListNoPriorityAsync({
            ...searchParams,
            _offset: rOffset,
            _limit: globalLimit,
            _sort: tableRules.sort,
          })
          .then((res) => {
            regularData = (res.result || []).map((item) => ({
              ...item,
              is_priority: item.is_priority === 1 ? 1 : 0,
            }));
          }),
      );
    }

    if (fetchTasks.length > 0) {
      await Promise.all(fetchTasks);
    }

    dataTables.value = [...priorityData, ...regularData];
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
  updateUrlQuery({ showFormSearch: tableRules.showFormSearch });
};

const fn_TimKiem = (formEl) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (!valid) return;
    Object.keys(tableRules.dataSearch.value).forEach((key) => {
      const val = tableRules.dataSearch.value[key];
      if (typeof val === "string")
        tableRules.dataSearch.value[key] = val.trim();
    });
    tableRules.page = 1;
    tableRules.offset = 0;
    getListData();
  });
};

const fn_tableSortChange = (column) => {
  MethodService.tableSortChange(column, tableRules);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  getListData();
};

const fn_tableCurentChange = (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const handleAdd = () => {
  dialogType.value = "add";
  iconType.value = "link";
  originalServiceName.value = "";
  Object.assign(editForm, MethodService.copyObject(DataForm.form));
  editVisible.value = true;
  nextTick(() => {
    if (editFormRef.value) {
      editFormRef.value.clearValidate();
    }
  });
};

const handleEdit = (row) => {
  dialogType.value = "edit";
  iconType.value = "link";
  originalServiceName.value = row.service_name;
  Object.keys(editForm).forEach((key) => {
    editForm[key] = row[key] !== undefined ? row[key] : DataForm.form[key];
  });
  editForm.id = row.id || row.service_id;
  editVisible.value = true;
  nextTick(() => {
    if (editFormRef.value) {
      editFormRef.value.clearValidate();
    }
  });
};

const submitEdit = async (formEl) => {
  if (!formEl) return;
  await formEl.validate(async (valid) => {
    if (valid) {
      saving.value = true;
      try {
        const payload = { ...editForm };
        payload.main_group_id = parseInt(payload.main_group_id);
        payload.is_priority = payload.is_priority ? 1 : 0;
        if (dialogType.value === "add") {
          await mushroom.service_health_ch.createAsync(payload);
          toastr.success(t("toastr_add_success"));
        } else {
          await mushroom.service_health_ch.partialUpdateAsync(payload);
          toastr.success(t("toastr_update_success"));
        }
        editVisible.value = false;
        await getListData();
      } catch (e) {
        const errorMap = {
          1002: "t_toastr_service_name_duplicate",
          1003: "t_toastr_service_name_in_use",
        };

        const subMsg = e?.code === 400 ? errorMap[e.subCode] : null;

        if (subMsg) {
          toastr.error(t(subMsg));
        } else {
          toastr.error(t("toastr_error"));
        }
      } finally {
        saving.value = false;
      }
    }
  });
};

const handleDPI = (row) => {
  router.push({
    name: "DpiPriorityService",
    params: { id: row.service_id },
  });
};

const handlePriorityChange = (row) => {
  Swal.fire({
    title: t("t_warning"),
    text:
      row.is_priority === 0
        ? t("swal_confirm_priority_on")
        : t("swal_confirm_priority_off"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        if (row.is_priority === 0) {
          await mushroom.service_health.markPriorityAsync({
            body: {
              ch_service_id: row.service_id,
            },
          });
        } else
          await mushroom.service_health.unmarkPriorityAsync({
            body: {
              ch_service_id: row.service_id,
            },
          });
        toastr.success(t("toastr_update_success"));
        getListData();
      } catch (e) {
        if (
          e?.code === 400 &&
          e?.subCode === 1003 &&
          e?.meta?.list_group?.length > 0
        ) {
          const listName = e.meta.list_group
            .map((item) => `<li>${item.name}</li>`)
            .join("");
          const htmlContent = DOMPurify.sanitize(
            `<div class="text-start">${t(
              "toastr_service_in_use_group_error",
            )}</div><ul class="text-start mt-2">${listName}</ul>`,
          );
          Swal.fire({
            title: t("t_warning"),
            html: htmlContent,
            icon: "warning",
            confirmButtonColor: "#34c38f",
            confirmButtonText: t("swal_accept"),
          });
        } else if (e?.code === 400 && e?.subCode === 1004) {
          toastr.error(t("t_maxium_prioty_services_limit"));
        } else {
          const errorMap = {
            1002: "t_toastr_service_name_duplicate",
            1003: "t_toastr_service_name_in_use",
          };

          const subMsg = e?.code === 400 ? errorMap[e.subCode] : null;

          if (subMsg) {
            toastr.error(t(subMsg));
          } else {
            toastr.error(t("toastr_error"));
          }
        }
      }
    }
  });
};

const handleDelete = (row) => {
  if (!row.service_id) return;
  Swal.fire({
    title: t("t_warning"),
    text: t("swal_confirm_delete_category"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await mushroom.service_health_ch.deleteAsync({
          id: row.service_id,
        });
        toastr.success(t("toastr_delete_success"));
        await getListData();
      } catch (e) {
        if (e?.subCode === 1003) {
          toastr.error(
            t("t_service_using_error_delete", {
              dpi_records: e?.meta?.dpi?.total,
            }),
          );
        } else MethodService.showError(e.code);
      }
    }
  });
};

const handleInfo = async () => {
  try {
    const { result } = await mushroom.service_health_ch.views.getInfoAsync();
    viewData.value = result[0]?.note_update;
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
  showViewDialog.value = true;
};

onMounted(async () => {
  syncStateFromUrl();
  await getCategories();
  await getListData();
});
</script>

<template>
  <Layout>
    <div class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h3 class="text-uppercase mb-0">
          {{ $t("t_service_management") }}
        </h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button class="btn btn-secondary btn-border" @click="handleInfo">
              <i class="ri-information-line"></i>
              Info
            </button>
            <button class="btn btn-secondary btn-border" @click="handleAdd">
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>

      <div class="card-body p-0 pb-3">
        <div class="main-body">
          <div class="main-body-search mx-3">
            <el-collapse-transition>
              <div v-show="tableRules.showFormSearch">
                <b-card class="b-shadow mb-0">
                  <el-form
                    ref="ruleFormRef"
                    :model="tableRules.dataSearch.value"
                    :rules="validForm.value"
                    label-position="top"
                    @submit.prevent
                  >
                    <b-row align-v="end">
                      <b-col md="4">
                        <el-form-item
                          :label="$t('t_service_name')"
                          prop="service_name"
                        >
                          <el-input
                            clearable
                            v-model="
                              tableRules.dataSearch.value['service_name']
                            "
                            :placeholder="$t('t_place_service_name')"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="4">
                        <el-form-item
                          :label="$t('t_home_page')"
                          prop="home_page"
                        >
                          <el-input
                            clearable
                            v-model="tableRules.dataSearch.value['home_page']"
                            :placeholder="$t('t_place_home_page')"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="4">
                        <el-form-item
                          :label="$t('t_create_by')"
                          prop="create_by"
                        >
                          <el-select
                            v-model="tableRules.dataSearch.value['create_by']"
                            :placeholder="$t('t_all')"
                            clearable
                            style="width: 100%"
                          >
                            <el-option label="System" value="System" />
                            <el-option label="Manual" value="Manual" />
                          </el-select>
                        </el-form-item>
                      </b-col>
                      <b-col md="4">
                        <el-form-item
                          :label="$t('t_is_priority')"
                          prop="is_priority"
                        >
                          <el-select
                            v-model="tableRules.dataSearch.value['is_priority']"
                            :placeholder="$t('t_all')"
                            clearable
                            style="width: 100%"
                          >
                            <el-option :label="$t('t_yes')" value="1" />
                            <el-option :label="$t('t_no')" value="0" />
                          </el-select>
                        </el-form-item>
                      </b-col>
                      <b-col md="4">
                        <el-form-item
                          :label="$t('t_main_group')"
                          prop="main_group_id"
                        >
                          <el-select
                            v-model="
                              tableRules.dataSearch.value['main_group_id']
                            "
                            :placeholder="$t('t_all')"
                            clearable
                            filterable
                            style="width: 100%"
                          >
                            <el-option
                              v-for="cat in categories"
                              :key="cat.group_id"
                              :label="cat.group_name"
                              :value="cat.group_id"
                            />
                          </el-select>
                        </el-form-item>
                      </b-col>
                      <b-col md="4">
                        <el-form-item label="&nbsp;">
                          <div class="d-flex justify-content-center">
                            <a
                              class="btn btn-soft-secondary btn-border me-2"
                              style="width: 80px"
                              @click="fn_showFormTimKiem()"
                            >
                              {{ $t("t-hide") }}
                            </a>
                            <button
                              class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                              style="width: 100px"
                              native-type="submit"
                              @click="fn_TimKiem(ruleFormRef)"
                            >
                              {{ $t("t-search") }}
                            </button>
                          </div>
                        </el-form-item>
                      </b-col>
                    </b-row>
                  </el-form>
                </b-card>
              </div>
            </el-collapse-transition>
          </div>

          <div class="d-flex justify-content-end mt-3 mb-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>

          <el-table
            size="small"
            max-height="520"
            stripe
            :data="dataTables.value"
            style="width: 100%"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column :label="$t('t-stt')" width="60" align="center">
              <template #default="scope">
                {{
                  scope.$index + (tableRules.page - 1) * tableRules.limit + 1
                }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('t_is_priority')"
              width="70"
              align="center"
            >
              <template #default="scope">
                <el-checkbox
                  :model-value="scope.row.is_priority === 1"
                  @change="() => handlePriorityChange(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column
              prop="service_name"
              :label="$t('t_service_name')"
              align="center"
              min-width="150"
              sortable="custom"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  <img
                    :src="
                      scope.row.icon_display
                        ? scope.row.icon_display
                        : scope.row.icon
                        ? scope.row.icon
                        : noneIcon
                    "
                    alt="icon-service"
                    height="25"
                    width="25"
                    class="me-2"
                  />{{ scope.row.service_name }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="home_page"
              :label="$t('t_home_page')"
              min-width="120"
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ scope.row.home_page }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="main_group_id"
              align="center"
              :label="$t('t_main_group')"
              min-width="100"
              sortable="custom"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ getGroupName(scope.row.main_group_id) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="description"
              :label="$t('t_description')"
              align="center"
              min-width="250"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.description }}</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="create_by"
              :label="$t('t_create_by')"
              align="center"
              width="120"
              sortable="custom"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.create_by === 'System'"
                  class="badge badge-soft-danger"
                  >System</span
                >
                <span
                  v-else-if="scope.row.create_by === 'Manual'"
                  class="badge badge-soft-secondary"
                  >Manual</span
                >
                <span v-else class="badge badge-soft-info">{{
                  scope.row.create_by
                }}</span>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('t-action')"
              width="120"
              align="center"
              fixed="right"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    class="box-item"
                    effect="light"
                    :content="$t('action_dpi')"
                    placement="top"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-info waves-effect waves-light btn-sm"
                      @click="handleDPI(scope.row)"
                    >
                      <i class="ri-mouse-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('action_option')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-success waves-effect waves-light btn-sm me-1"
                      :disabled="
                        scope.row.create_by === 'System' ||
                        scope.row.permit_edit === 0
                      "
                      @click="handleEdit(scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('action_delete')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      :disabled="
                        scope.row.is_priority === 1 ||
                        scope.row.permit_delete === 0 ||
                        scope.row.create_by !== 'Manual'
                      "
                      @click="handleDelete(scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="d-flex justify-content-end mt-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Edit Service -->
    <el-dialog
      v-model="editVisible"
      :title="dialogTitle"
      width="600px"
      append-to-body
      align-center
      :close-on-click-modal="false"
    >
      <el-form
        ref="editFormRef"
        :model="editForm"
        :rules="editRules"
        label-position="top"
      >
        <b-row>
          <b-col md="12">
            <el-form-item prop="icon_display" class="label-full-width">
              <template #label>
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                  "
                >
                  <span
                    style="
                      color: var(--el-text-color-regular);
                      font-weight: 500;
                    "
                    >{{ $t("t_icon") }}</span
                  >
                  <el-radio-group v-model="iconType" size="small">
                    <el-radio-button label="link">{{
                      $t("t_enter_link")
                    }}</el-radio-button>
                    <el-radio-button label="upload">{{
                      $t("t_upload_image")
                    }}</el-radio-button>
                  </el-radio-group>
                </div>
              </template>
              <div class="d-flex align-items-center">
                <div class="flex-grow-1 me-2">
                  <el-input
                    v-if="iconType === 'link'"
                    v-model="editForm.icon_display"
                    :placeholder="$t('t_place_icon_url')"
                  />
                  <div v-else>
                    <button
                      type="button"
                      class="btn btn-secondary btn-border w-100"
                      @click="fileInput.click()"
                    >
                      <i class="ri-upload-2-line"></i>
                      {{ $t("t_select_image") }}
                    </button>
                    <input
                      type="file"
                      ref="fileInput"
                      style="display: none"
                      accept="image/*"
                      @change="handleFileUpload"
                    />
                  </div>
                </div>
                <img
                  :src="editForm.icon_display || noneIcon"
                  alt="preview"
                  height="40"
                  width="40"
                  style="object-fit: contain"
                />
              </div>
            </el-form-item>
          </b-col>
          <b-col md="6">
            <el-form-item :label="$t('t_service_name')" prop="service_name">
              <el-input
                v-model="editForm.service_name"
                :placeholder="$t('t_place_service_name')"
              />
            </el-form-item>
          </b-col>
          <b-col md="6">
            <el-form-item :label="$t('t_main_group')">
              <el-select
                v-model="editForm.main_group_id"
                style="width: 100%"
                filterable
                :placeholder="$t('t_select_main_group')"
              >
                <el-option
                  v-for="cat in categories"
                  :key="cat.group_id"
                  :label="cat.group_name"
                  :value="cat.group_id"
                />
              </el-select>
            </el-form-item>
          </b-col>
          <b-col md="12">
            <el-form-item :label="$t('t_home_page')" prop="home_page">
              <el-input
                v-model="editForm.home_page"
                :placeholder="$t('t_place_home_page')"
              />
            </el-form-item>
          </b-col>
          <b-col md="12">
            <el-form-item :label="$t('t_description')" prop="description">
              <el-input
                v-model="editForm.description"
                type="textarea"
                :rows="2"
                :placeholder="$t('t_place_description')"
              />
            </el-form-item>
          </b-col>
        </b-row>
      </el-form>
      <template #footer>
        <div class="text-center">
          <button
            class="btn btn-secondary btn-border"
            @click="submitEdit(editFormRef)"
            :loading="saving"
          >
            {{ dialogType === "add" ? $t("t-add") : $t("t_update") }}
          </button>
        </div>
      </template>
    </el-dialog>

    <!-- Dialog View -->
    <el-dialog
      v-model="showViewDialog"
      width="60%"
      :title="$t('t_info_update_dpi')"
      :close-on-click-modal="false"
      destroy-on-close
      align-center
    >
      <div
        class="markdown-content"
        v-html="markdownToHTML(viewData || '')"
      ></div>
    </el-dialog>
  </Layout>
</template>
<style scoped>
:deep(.label-full-width .el-form-item__label) {
  width: 100% !important;
}
</style>
